import { FC } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toggleFeedMedia } from "actions/sourceMedia";
import { getSelectedFeedIds } from "selectors/hashtagFeedSelectors";
import { showViewHashtagFeedItemModal } from "actions/modal";
import GridMediaCard from "components/MediaLibrary/GridMediaCard/GridMediaCard";
import moment from "moment";
import { NumericFormat } from "react-number-format";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import { CollectionFile } from "@/shared/types/files";

export interface FeedMedia {
  id: string;
  caption?: string;
  media_url: string;
  thumbnail?: string;
  media_type: "VIDEO" | "IMAGE" | "CAROUSEL_ALBUM";
  timestamp?: string;
  like_count?: number;
  comments_count?: number;
  permalink?: string;
  username?: string;
}

interface FeedGridProps {
  feedMedia: FeedMedia[];
}

// Adapter to convert feed media to CollectionFile format
export const assetFromFeedMedia = (media: FeedMedia): CollectionFile => ({
  _id: media.id,
  asset_id: media.id,
  source: {
    name: "Instagram Feed",
    value: "feed",
  },
  name: media.caption || "Feed Media",
  description: media.caption || "",
  tags: [],
  favorite: false,
  active: true,
  storage: {
    bucket: "",
    key: "",
    url: media.media_url,
  },
  thumbnail: media.thumbnail || media.media_url,
  downloadUrl: media.media_url,
  gifThumbnail: media.media_type === "VIDEO" ? media.thumbnail : undefined,
  inbox_id: "",
  created: new Date(media.timestamp || Date.now()),
  updated: new Date(media.timestamp || Date.now()),
  meta: {
    mime: media.media_type === "VIDEO" ? "video/mp4" : "image/jpeg",
    format: media.media_type === "VIDEO" ? "mp4" : "jpeg",
    size: 0,
    width: 0,
    height: 0,
    space: "srgb",
    channels: 3,
    depth: "uchar",
    density: 72,
    isProgressive: false,
    hasProfile: false,
    hasAlpha: false,
    isVideo: media.media_type === "VIDEO",
    isImage:
      media.media_type === "IMAGE" || media.media_type === "CAROUSEL_ALBUM",
    google: { labels: [] },
    chromaSubsampling: "4:2:0",
    exif: {},
  },
  credential: {
    admin: false,
    auth_key: "",
    ext_auth: false,
  },
  original: true,
  __v: 0,
  usedInDraftPosts: [],
  usedInUpcomingPosts: [],
});

const FeedGrid: FC<FeedGridProps> = ({ feedMedia }) => {
  const dispatch = useDispatch();
  const selectedFeedIds = useSelector(getSelectedFeedIds);

  const getHoverContent = (media: FeedMedia) => (
    <div className="flex flex-col h-full">
      <div className="flex pt-2 pl-2 pr-2 pb-1 h-full items-center cursor-default">
        <div className="flex flex-wrap">
          <div className="flex items-center text-white">
            <i className="fa-regular fa-heart text-white text-sm" />
            <span className="ml-1 mr-2 text-base leading-tight">
              <NumericFormat
                value={media.like_count}
                displayType={"text"}
                thousandSeparator={true}
              />
            </span>
          </div>
          <div className="flex items-center text-white">
            <i className="fa-regular fa-comment text-white text-sm" />
            <span className="ml-1 mr-2 text-base leading-tight">
              <NumericFormat
                value={media.comments_count}
                displayType={"text"}
                thousandSeparator={true}
              />
            </span>
          </div>
        </div>
        <div className="flex-grow flex justify-end font-semibold">
          <div className="mr-2 leading-none">
            {media.permalink ? (
              <a
                href={media.permalink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white hover:text-white/80"
              >
                <i className="fa-regular fa-external-link text-sm" />
              </a>
            ) : (
              <a
                href={media.media_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white hover:text-white/80"
              >
                <i className="fa-regular fa-arrow-up-right-from-square text-sm" />
              </a>
            )}
          </div>
          <div className="leading-none">
            <Tooltip>
              <TooltipTrigger asChild>
                <i
                  className="fa-regular fa-eye text-white cursor-pointer hover:text-white/80 text-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatch(showViewHashtagFeedItemModal(media));
                  }}
                />
              </TooltipTrigger>
              <TooltipContent side="bottom">Details</TooltipContent>
            </Tooltip>
          </div>
        </div>
      </div>
      {media.timestamp && (
        <div className="flex items-center text-sm pl-2 text-white/90">
          {moment(media.timestamp).fromNow()}
        </div>
      )}
    </div>
  );

  const handleCardClick = (media: FeedMedia) => {
    dispatch(toggleFeedMedia(media));
  };

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2.5">
      {feedMedia.map((media) => {
        const isSelected = selectedFeedIds.includes(media.id);

        if (!media.media_url) {
          return null;
        }

        return (
          <GridMediaCard
            key={media.id}
            asset={assetFromFeedMedia(media)}
            canFavorite={false}
            selected={isSelected}
            selecting={selectedFeedIds.length > 0}
            hoverContent={getHoverContent(media)}
            onCardClick={() => handleCardClick(media)}
            showActionButtons={false}
            username={media.username}
          />
        );
      })}
    </div>
  );
};

export default FeedGrid;
